'use client';

import { useEffect, useState } from 'react';
import { ArrowLeft, X, PenSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { useTranslation } from '@/components/i18n-provider';
import {
  CreatePostForm,
  CreateChannelForm,
} from '@/components/create/create-forms';

// Agent creation has its own flow (openCreateWithDialog('agent') — the
// environment picker), so this dialog is Post / Channel only.
type Tab = 'post' | 'channel';

type OpenDetail = {
  tab: Tab;
  preselectedChannel?: string;
  // Widget publish flow: attach an owned widget content row so the post form
  // publishes a frozen version (listed or unlisted) instead of a plain post.
  attachWidgetContentId?: string;
  attachWidgetName?: string;
};

const EVENT_NAME = 'workspace:open-create';

/**
 * Imperative API for showing the workspace Create dialog.
 *
 * The /create standalone page is being retired — Zap panel CTAs
 * ("Create post", "Create channel") and the workspace "+" FAB now
 * open this dialog instead of routing to /create. Inside the dialog
 * the user sees a clean header (title — not the URL), the Post /
 * Channel tabs, and the matching form. On
 * success the form fires its `onSuccess` callback and the dialog
 * closes without ever navigating the user away from the canvas.
 */
export function openWorkspaceCreateDialog(
  tab: Tab = 'post',
  opts?: { preselectedChannel?: string; attachWidgetContentId?: string; attachWidgetName?: string },
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<OpenDetail>(EVENT_NAME, {
      detail: {
        tab,
        ...(opts?.preselectedChannel ? { preselectedChannel: opts.preselectedChannel } : {}),
        ...(opts?.attachWidgetContentId ? { attachWidgetContentId: opts.attachWidgetContentId } : {}),
        ...(opts?.attachWidgetName ? { attachWidgetName: opts.attachWidgetName } : {}),
      },
    }),
  );
}

/**
 * Mounted once per workspace shell. Listens for
 * `workspace:open-create` and renders the matching form inside a
 * glass-panel dialog. Esc and the X button close it; on success the
 * form invokes our `onSuccess` and we collapse the dialog so the user
 * lands back on the canvas with the new artefact already visible
 * (forms still fire their `*Created` window events on success so any
 * listening surface — Zap activity feed, library — can refresh).
 */
export function CreateDialog() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('post');
  const [preselectedChannel, setPreselectedChannel] = useState<string | undefined>(undefined);
  const [attachWidgetContentId, setAttachWidgetContentId] = useState<string | undefined>(undefined);
  const [attachWidgetName, setAttachWidgetName] = useState<string | undefined>(undefined);
  const { t } = useTranslation();

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenDetail>).detail;
      if (detail?.tab) setTab(detail.tab);
      setPreselectedChannel(detail?.preselectedChannel);
      setAttachWidgetContentId(detail?.attachWidgetContentId);
      setAttachWidgetName(detail?.attachWidgetName);
      setOpen(true);
    };
    window.addEventListener(EVENT_NAME, onOpen);
    return () => window.removeEventListener(EVENT_NAME, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const close = () => setOpen(false);

  const tabs: Array<{ id: Tab; labelKey: string; icon: React.ReactNode }> = [
    { id: 'post', labelKey: 'post', icon: <PenSquare className="h-4 w-4" /> },
    {
      id: 'channel',
      labelKey: 'channel',
      icon: <span className="font-mono text-primary text-sm">//</span>,
    },
  ];

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel="Create"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 220,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 4vw',
      }}
      className="bg-black/40 backdrop-blur-sm"
    >
      <div
        className="glass-panel relative flex h-[min(720px,90vh)] w-[min(720px,96vw)] flex-col overflow-hidden rounded-2xl shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/*
          Header — friendly title + close button. NO URL displayed,
          NO "open in new tab" link: the workspace is now the only
          UI surface for create flows, so a deep-link affordance
          would just confuse.
        */}
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <h2 className="text-sm font-semibold tracking-tight">
            {t('create.title')}
          </h2>
          <div className="ml-auto" />
          <button
            type="button"
            aria-label={t('workspace.canvas.node.close')}
            onClick={close}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs — Post / Channel (agent creation has its own picker). */}
        <div className="shrink-0 px-4 pt-3">
          <div className="flex gap-1 rounded-xl bg-muted p-1">
            {tabs.map(({ id, labelKey, icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition-all @sm/main:text-sm',
                  tab === id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {icon}
                <span>{t(`create.${labelKey}`)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Body — the matching form, scrollable. `onSuccess` closes
            the dialog instead of routing away from the workspace. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
          {tab === 'post' && (
            <CreatePostForm
              preselectedChannel={preselectedChannel}
              attachWidgetContentId={attachWidgetContentId}
              attachWidgetName={attachWidgetName}
              onSuccess={close}
            />
          )}
          {tab === 'channel' && (
            <CreateChannelForm
              parentSlug={preselectedChannel}
              onSuccess={close}
            />
          )}
        </div>
      </div>
    </PopoverPortal>
  );
}

// Re-export the back arrow so we can keep the lucide import surface
// small if a follow-up wants a back affordance for nested flows.
export { ArrowLeft };
